import {
  Autocomplete,
  type AutocompleteProps,
  type AutocompleteValue,
  TextField,
} from '@mui/material';
import { useId } from 'react';

import { FormField, type RequireLabelOrAria, getLabelOrAriaProps } from 'common';

import { POPOVER_FIELD_MINIMUM_WIDTH } from '../../constants';

type AutocompleteFieldBaseProps<TValue, TMultiple extends boolean | undefined> = Omit<
  AutocompleteProps<TValue, TMultiple, false, false>,
  'renderInput' | 'aria-label'
> & {
  getOptionLabel: (option: TValue) => string;
} & RequireLabelOrAria;

export function AutocompleteField<TValue, TMultiple extends boolean | undefined>(
  props: AutocompleteFieldBaseProps<TValue, TMultiple>
) {
  const id = useId();
  const { label: _discardLabel, value, multiple, ...fieldProps } = props;
  const { getOptionLabel } = fieldProps;

  return (
    <FormField {...getLabelOrAriaProps(props)} id={id}>
      <Autocomplete
        id={id}
        renderOption={(props, option, state) => (
          /**
           * `renderOption` is required to ensure that the filtered items have unique keys.
           * See this article: https://lockstep.io/blog/debugging-lesson-well-learned-fixing-material-ui-autocomplete-search/
           */
          <li {...props} key={state.index}>
            {getOptionLabel?.(option)}
          </li>
        )}
        {...fieldProps}
        multiple={multiple}
        value={
          multiple ? (value ?? ([] as AutocompleteValue<TValue, TMultiple, false, false>)) : value
        }
        renderInput={(params) => <TextField {...params} size="small" autoFocus />}
        sx={{
          minWidth: POPOVER_FIELD_MINIMUM_WIDTH,
        }}
      />
    </FormField>
  );
}
